import React from "react";
import { Box, Flex, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text, Tooltip } from "@chakra-ui/react";
import SocketIcon from "../../../assets/icons/Socket";
import { Toggle } from "../../automations/components/Toggle";
import { SunOutline } from "../../../assets/icons/Brightness";
import { useDebouncedCallback } from "../../../utils/helpers";
import { useToggleDeviceMutation } from "../../automations/api/automationsApi";

interface Props {
    device: any;
    title: string;
    toggle?: boolean
    parameter?: string
    brightness?: number
    onBrightnessChange?: (value: number) => void
    modelChannelTopic?: number
}

export function Card({ device, title, parameter, toggle, brightness, onBrightnessChange, modelChannelTopic }: Props) {
    const [toggleDeviceById, { isLoading: isToggleDeviceLoading }] = useToggleDeviceMutation()

    const debouncedPostBrightness = useDebouncedCallback((value: number) => {
        toggleDeviceById({
            device_id: device.id,
            action: 'brightness',
            brightness: value,
            topic_id: modelChannelTopic
        })
    }, 300)

    const handleToggle = (value) => {
        toggleDeviceById({
            device_id: device.id,
            action: value ? 'on' : 'off',
            topic_id: modelChannelTopic
        })
    }

    const handleBrightnessChange = (value) => {
        onBrightnessChange(value)
        debouncedPostBrightness(value)
    }
    return (
        <Flex
            direction="column"
            alignItems="center"
            key={device.id}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="#E2E8F0"
            height="172px"
            py={2}
            justifyContent="space-between"
        >
            <Flex direction="column" alignItems="center">
                <SocketIcon boxSize="24px" />
                <Tooltip label={title} placement="top" hasArrow>
                    <Text mt={3} fontWeight="bold" maxW="150px" isTruncated>
                        {title}
                    </Text>
                </Tooltip>
            </Flex>
            {typeof brightness === 'number' && onBrightnessChange && (
                <Box w="full" px={2}>
                    <Box
                        position="relative"
                        h="52px"
                        borderRadius="8px"
                        overflow="hidden"
                        bg="#E0E0E0"
                    >
                        <Box
                            position="absolute"
                            left={0}
                            top={0}
                            bottom={0}
                            width={`${brightness}%`}
                            bgGradient="linear(to-r, #1daafc, #1683ef)"
                            transition="width 0.2s ease"
                        />
                        <Flex
                            position="absolute"
                            top={0}
                            bottom={0}
                            left={0}
                            right={0}
                            align="center"
                            justify="center"
                            zIndex={1}
                            pointerEvents="none"
                        >
                            <Text fontSize="sm" color="white" fontWeight="semibold">
                                <SunOutline /> {brightness}%
                            </Text>
                        </Flex>
                        <Slider
                            value={brightness}
                            onChange={handleBrightnessChange}
                            min={0}
                            max={100}
                            step={1}
                            w="full"
                            h="full"
                            opacity={0}
                            position="absolute"
                            top={0}
                            left={0}
                            zIndex={2}
                        >
                            <SliderTrack>
                                <SliderFilledTrack />
                            </SliderTrack>
                            <SliderThumb />
                        </Slider>
                    </Box>
                </Box>
            )}
            {toggle !== undefined && (
                <Toggle
                    handleClick={handleToggle}
                    isActive={toggle as boolean}
                    isLoading={isToggleDeviceLoading}
                />
            )}
        </Flex>
    );
}
